"use client";

import * as React from "react";
import { CheckIcon, MinusIcon } from "lucide-react";
import { Checkbox as CheckboxPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";
import { refusalProps, withDisabledReason } from "./disabled-reason";

/* The Sirdab checkbox. Independent on/off picks that record with the form:
   list selections, agreements, options. If the flip should take effect NOW,
   that is a Switch; if it is one choice out of a few, a RadioGroup.

   Chrome: a 16px box with a 1px border-interactive border (the retained
   alias of --input, the input hairline) and a radius-xs corner.
   The border walks the FORM ladder: rest alpha-200, hover one rung deeper
   (input-border-hover-form, alpha-300), gated to unchecked so the ink
   checked state is untouched. Checked steps border and fill to primary
   ink and the check glyph goes primary-foreground, the same ink a
   switched-on Switch takes. Box size, radius pick, and the glyphs are
   [interim]: composed from the system's own input tokens.

   The glyph FADES at 75ms rather than snapping: always-rendered with the
   opacity toggled by state, the same fade pattern as the select
   check. Radix mounts the Indicator per state, so the fade needs
   forceMount plus in-data toggles on the glyphs.

   Indeterminate is the third real state, not a hidden one: same ink fill,
   a minus glyph. Without an explicit style Radix would show the CHECK for
   it, which is the one signal a tri-state must never send.

   Disabled dims to 50% [interim, the Radix convention].
   Focus is the global 2px ink focus-visible outline: the root
   renders a <button>, so it comes free, and nothing here may set
   outline-none or keyboard focus disappears.

   Wire it through Label's inline form: wrap the control in the label,
   never sibling the two with a hand-rolled flex row.

   Usage:
       <Label htmlFor="vat">
           <Checkbox id="vat" defaultChecked /> Charge VAT on this product
       </Label>
       <Label htmlFor="terms">
           <Checkbox id="terms" required /> I agree to the terms
       </Label>
       <Checkbox checked="indeterminate" aria-label="Select all" />
*/
function Checkbox({
  className,
  disabledReason,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  /** WHY this is unavailable, on a tooltip. Implies disabled, and
        keeps the control reachable so the reason can actually be read
        (see disabled-reason.tsx). */
  disabledReason?: React.ReactNode;
}) {
  const control = (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer border-border-interactive data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground hover:data-[state=unchecked]:border-input-border-hover-form size-4 shrink-0 cursor-pointer rounded-xs border transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
      {...refusalProps(disabledReason)}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        forceMount
        className="grid place-content-center text-current"
      >
        <span className="relative block size-3">
          <CheckIcon className="absolute inset-0 size-3 opacity-0 transition-opacity duration-75 in-data-[state=checked]:opacity-100" />
          <MinusIcon className="absolute inset-0 size-3 opacity-0 transition-opacity duration-75 in-data-[state=indeterminate]:opacity-100" />
        </span>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  return withDisabledReason(control, disabledReason);
}

export { Checkbox };
